import { useEffect, useState } from "react";
import { api } from "@/lib/api";

// Module-level guard: survives React 18 StrictMode's simulated remount
// (where useRef IS reset). Keyed by session_id so a fresh sign-in in the
// same tab still works.
const processedSessionIds = new Set();
let anySessionProcessed = false;

export default function AuthCallback() {
  const [error, setError] = useState(null);

  useEffect(() => {
    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const sessionId = params.get("session_id");

    if (!sessionId) {
      // Second StrictMode invoke after we cleared the hash: silently ignore.
      // Also covers the legitimate "no session in URL" case, but only when
      // no prior successful exchange has run in this tab.
      if (anySessionProcessed) return;
      window.location.replace("/");
      return;
    }

    if (processedSessionIds.has(sessionId)) return;
    processedSessionIds.add(sessionId);

    (async () => {
      try {
        await api.post("/auth/session", { session_id: sessionId });
        anySessionProcessed = true;
        // 1) Clear the fragment so the reloaded page doesn't re-enter AuthCallback.
        // 2) Force a hard reload so the freshly-set httpOnly cookie is picked up
        //    by every subsequent request. window.location.replace("/dashboard")
        //    is unreliable here because the current URL is /dashboard#session_id=…
        //    and browsers treat that as a hash-only change (no reload).
        window.history.replaceState(null, "", "/dashboard");
        window.location.reload();
      } catch (e) {
        const detail = e?.response?.data?.detail || e?.message || "Sign-in failed";
        window.history.replaceState(null, "", "/");
        setError(detail.toString());
      }
    })();
  }, []);

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
        <div data-testid="auth-callback-error" className="max-w-md text-center space-y-4">
          <div className="text-slate-900 font-heading text-2xl font-semibold">Couldn&rsquo;t sign you in</div>
          <div className="text-sm text-slate-600 border border-rose-200 bg-rose-50 rounded-md p-3">
            <div className="font-medium text-rose-800 mb-1">Error from server</div>
            <div className="text-slate-700 font-mono text-xs whitespace-pre-wrap">{error}</div>
          </div>
          <div className="flex gap-2 justify-center">
            <a
              href="/"
              data-testid="back-to-login-btn"
              className="inline-flex items-center rounded-md bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 text-sm transition-colors"
            >
              Back to sign in
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center bg-slate-50">
      <div data-testid="auth-callback" className="text-slate-500 text-sm">Signing you in…</div>
    </div>
  );
}
